import {
  ScrollView,
  StyleSheet,
  View,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import {
  Text,
  Card,
  IconButton,
  Searchbar,
  Chip,
  Avatar,
  Surface,
} from "react-native-paper";
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, isValid, parseISO } from "date-fns";
import { useAppSelector } from "../../redux/store";
import { User } from "../../redux/slices/authSlice";
import { getInvitations } from "../../services/employeeServices";
import { listenCompanyDocuments } from "../../services/firestoreServices";
import { JoinRequestsList } from "./JoinRequestsList";
import { DrawerScreenProps } from "@react-navigation/drawer";
import {
  DrawerParamList,
  RootStackParamList,
} from "../../types/navigationParams";
import { CompositeScreenProps } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { USERS_COLLECTION } from "../../constants/collections";
import { ValueDisplay } from "../../components/ValueDisplay";
import UserAvatar from "../../components/UserAvatar";

export interface Employee extends Omit<User, "uid"> {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;

  [key: string]: any;
}

type Props = CompositeScreenProps<
  DrawerScreenProps<DrawerParamList, "EmployeesScreen">,
  StackScreenProps<RootStackParamList>
>;

export default function EmployeesScreen({ navigation }: Props) {
  const config = useAppSelector((state) => state.auth.config);
  const userRole = useAppSelector((state) => state.auth.user.role);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [invitations, setInvitations] = useState<Employee[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  const usersCollection = config.database.find(
    (c) => c.name === USERS_COLLECTION,
  );

  // Filter employees based on search query
  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return employees;

    return employees.filter((employee) => {
      const searchLower = searchQuery.toLowerCase();
      const fullName = (employee.fullName || employee.name || "").toLowerCase();
      const email = (employee.email || "").toLowerCase();
      const role = (employee.role || "").toLowerCase();
      const roleLabel = t(employee.role || "").toLowerCase();
      const block = (employee.block || "").toLowerCase();
      return (
        fullName.includes(searchLower) ||
        email.includes(searchLower) ||
        role.includes(searchLower) ||
        roleLabel.includes(searchLower) ||
        block.includes(searchLower)
      );
    });
  }, [employees, searchQuery, t]);

  // Filter invitations
  const filteredInvitations = useMemo(() => {
    return invitations.filter(
      (i) =>
        !employees.some((e) => e.email.toLowerCase() === i.email.toLowerCase()),
    );
  }, [invitations, employees]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const invitationsData = await getInvitations();
        setInvitations(invitationsData);
      } catch (error) {
        console.error("Error fetching invitations:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();

    // Set up real-time listener for employees
    const unsubscribe = listenCompanyDocuments(
      USERS_COLLECTION,
      (data: Employee[]) => {
        setEmployees(data);
        setIsLoading(false);
      },
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  const handleEditEmployee = (employee: Employee) => {
    navigation.navigate("EmployeeDetailsScreen", { employee });
  };

  const navToPicker = (options: any) => {
    navigation.navigate("PickerScreen", options);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "standalone":
      case "granted":
        return "#4CAF50";
      case "browser":
        return "#2196F3";
      case "denied":
      case "default":
        return "#F44336";
      default:
        return "#9E9E9E";
    }
  };

  const formatLastLogin = (value: any) => {
    if (!value) return "-";

    const date =
      typeof value?.toDate === "function" ? value.toDate() : new Date(value);

    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString();
  };

  const getPlatformLastLogin = (
    employee: Employee,
    platform: "web" | "ios" | "android",
  ) => {
    const byPlatform = employee.lastLoginByPlatform?.[platform];
    if (byPlatform) return byPlatform;

    if (employee.lastLoginPlatform === platform && employee.lastLoginAt) {
      return employee.lastLoginAt;
    }

    return null;
  };

  const getRoleColor = (role?: string) => {
    switch ((role || "").toLowerCase()) {
      case "admin":
        return "#6D28D9";
      case "manager":
        return "#0F766E";
      case "master":
        return "#B45309";
      case "employee":
        return "#1D4ED8";
      default:
        return "#475569";
    }
  };

  const renderEmployeeCard = (employee: Employee) => {
    const displayName = employee.fullName || employee.name || employee.email;

    return (
      <Card key={employee.email} style={styles.employeeCard} mode="outlined">
        <TouchableOpacity
          onPress={() => {
            if (config.employeesEditableBy?.includes(userRole)) {
              handleEditEmployee(employee);
            }
          }}
          disabled={!config.employeesEditableBy?.includes(userRole)}
          activeOpacity={
            config.employeesEditableBy?.includes(userRole) ? 0.7 : 1
          }
        >
          <Card.Content style={styles.cardContent}>
            <View style={styles.employeeHeader}>
              <View style={styles.employeeInfo}>
                <UserAvatar
                  size={48}
                  avatarUrl={employee.avatarUrl}
                  fullName={displayName}
                  email={employee.email}
                  style={styles.avatar}
                />
                <View style={styles.employeeDetails}>
                  <Text variant="titleMedium" style={styles.employeeName}>
                    {displayName}
                  </Text>
                  {employee.role && (
                    <Chip
                      mode="outlined"
                      compact
                      style={[
                        styles.roleChip,
                        { backgroundColor: getRoleColor(employee.role) + "16" },
                      ]}
                      textStyle={{ color: getRoleColor(employee.role) }}
                    >
                      {t(employee.role)}
                    </Chip>
                  )}
                </View>
              </View>
              {config.employeesEditableBy?.includes(userRole) && (
                <IconButton
                  icon="pencil"
                  size={20}
                  iconColor="#666"
                  onPress={() => handleEditEmployee(employee)}
                />
              )}
            </View>
          </Card.Content>
        </TouchableOpacity>
      </Card>
    );
  };

  const renderInvitationCard = (invitation: Employee) => (
    <Card key={invitation.id} style={styles.invitationCard} mode="outlined">
      <Card.Content style={styles.cardContent}>
        <View style={styles.invitationHeader}>
          <Avatar.Icon size={40} icon="email" style={styles.invitationAvatar} />
          <View style={styles.invitationDetails}>
            <Text variant="titleSmall">{invitation.email}</Text>
            <Text variant="bodySmall" style={styles.invitationRole}>
              {t(invitation.role)}
            </Text>
          </View>
          <Chip
            mode="outlined"
            compact
            style={[
              styles.statusChip,
              { backgroundColor: getStatusColor(invitation.status) + "20" },
            ]}
            textStyle={{ color: getStatusColor(invitation.status) }}
          >
            {t(invitation.status)}
          </Chip>
        </View>
      </Card.Content>
    </Card>
  );

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" />
        <Text variant="bodyLarge" style={styles.loadingText}>
          {t("Loading employees...")}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.content}>
          {/* Header */}
          <Surface style={styles.header} elevation={0}>
            <View style={styles.titleRow}>
              <View>
                <Text variant="headlineMedium" style={styles.title}>
                  {t("Employees")}
                </Text>
              </View>
              <Avatar.Icon
                size={46}
                icon="account-group"
                style={styles.headerIcon}
              />
            </View>
          </Surface>

          {/* Search Bar */}
          <Searchbar
            placeholder={t("Search employees...")}
            onChangeText={setSearchQuery}
            value={searchQuery}
            style={styles.searchBar}
            inputStyle={styles.searchInput}
            iconColor="#0f172a"
            elevation={0}
          />

          {/* Employees List */}
          {filteredEmployees.length > 0 ? (
            <View style={styles.employeesSection}>
              <Text variant="titleMedium" style={styles.sectionTitle}>
                {t("Team Members")}
              </Text>
              <View style={styles.employeesList}>
                {filteredEmployees.map((employee) =>
                  renderEmployeeCard(employee),
                )}
              </View>
            </View>
          ) : (
            <View style={styles.emptyState}>
              <IconButton icon="account-group" size={64} iconColor="#ccc" />
              <Text variant="titleMedium" style={styles.emptyTitle}>
                {searchQuery ? t("No employees found") : t("No employees yet")}
              </Text>
              <Text variant="bodyMedium" style={styles.emptySubtitle}>
                {searchQuery
                  ? t("Try adjusting your search terms")
                  : t("Employees will appear here once they join")}
              </Text>
            </View>
          )}

          {/* Invitations Section */}
          {filteredInvitations.length > 0 && (
            <View style={styles.invitationsSection}>
              <Text variant="titleMedium" style={styles.sectionTitle}>
                {t("Pending Invitations")}
              </Text>
              <View style={styles.invitationsList}>
                {filteredInvitations.map((invitation) =>
                  renderInvitationCard(invitation),
                )}
              </View>
            </View>
          )}

          {/* Join Requests */}
          <JoinRequestsList navToPicker={navToPicker} />
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f1f5f9",
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
    maxWidth: 920,
    alignSelf: "center",
    width: "100%",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f8f9fa",
  },
  loadingText: {
    marginTop: 16,
    color: "#666",
  },
  header: {
    marginBottom: 18,
    borderRadius: 18,
    backgroundColor: "#ffffff",
    padding: 16,
    borderWidth: 1,
    borderColor: "#e2e8f0",
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerIcon: {
    backgroundColor: "#DBEAFE",
  },
  title: {
    fontWeight: "bold",
    color: "#0f172a",
  },
  searchBar: {
    marginBottom: 24,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#dbe2ea",
    backgroundColor: "#ffffff",
  },
  searchInput: {
    fontSize: 16,
  },
  employeesSection: {
    marginBottom: 24,
  },
  invitationsSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontWeight: "600",
    color: "#0f172a",
    marginBottom: 12,
  },
  employeesList: {
    gap: 12,
  },
  invitationsList: {
    gap: 8,
  },
  employeeCard: {
    marginBottom: 0,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 14,
    backgroundColor: "#ffffff",
  },
  invitationCard: {
    marginBottom: 0,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 12,
    backgroundColor: "#ffffff",
  },
  cardContent: {
    padding: 16,
  },
  employeeHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  employeeInfo: {
    flexDirection: "row",
    alignItems: "flex-start",
    flex: 1,
  },
  avatar: {
    marginRight: 12,
    backgroundColor: "#e3f2fd",
  },
  employeeDetails: {
    flex: 1,
  },
  employeeName: {
    fontWeight: "600",
    color: "#0f172a",
    marginBottom: 4,
  },
  employeeEmail: {
    color: "#64748b",
    marginBottom: 8,
  },
  birthdayText: {
    color: "#64748b",
    marginBottom: 8,
  },
  roleChip: {
    alignSelf: "flex-start",
    borderWidth: 1,
    borderColor: "transparent",
  },
  fieldsContainer: {
    marginBottom: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#eef2f7",
  },
  fieldRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  fieldLabel: {
    fontWeight: "500",
    color: "#64748b",
    minWidth: 120,
    marginRight: 8,
  },
  fieldValue: {
    flex: 1,
  },
  loginPlatformsContainer: {
    marginTop: 14,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: "#eef2f7",
    gap: 6,
  },
  loginPlatformsTitle: {
    color: "#64748b",
    fontWeight: "600",
  },
  loginTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  versionValue: {
    color: "#64748b",
  },
  loginPlatformRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  loginPlatformLabel: {
    color: "#475569",
    fontWeight: "500",
  },
  loginPlatformValue: {
    color: "#0f172a",
  },
  invitationHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  invitationAvatar: {
    marginRight: 12,
    backgroundColor: "#fff3e0",
  },
  invitationDetails: {
    flex: 1,
  },
  invitationRole: {
    color: "#64748b",
    marginTop: 2,
  },
  statusChip: {
    alignSelf: "flex-start",
    borderWidth: 1,
    borderColor: "transparent",
  },
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    color: "#334155",
    marginTop: 16,
    textAlign: "center",
  },
  emptySubtitle: {
    color: "#64748b",
    marginTop: 8,
    textAlign: "center",
    lineHeight: 20,
  },
  // Legacy styles for compatibility
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  bold: {
    fontWeight: "bold",
  },
  card: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
});
